import {
  isAddressNumberToken,
  isPostcodeToken,
  isUnitMarker,
  normaliseAddressQuery,
  parseAddressQuery,
  tokeniseAddressQuery
} from "./normalise.js";
import type { ParsedAddressRange } from "./normalise.js";
import type { CompactShardAddress, StreetKitSearchResult } from "./types.js";

export type RankedStreetKitCandidate = StreetKitSearchResult & {
  record: CompactShardAddress;
};

export type RankCandidatesOptions = {
  rawQuery: string;
  datasetVersion: string;
  limit?: number;
};

export function rankCandidates(
  records: CompactShardAddress[],
  options: RankCandidatesOptions
): RankedStreetKitCandidate[] {
  const parsedQuery = parseAddressQuery(options.rawQuery);
  const tokens = parsedQuery.tokens;

  if (tokens.length === 0) {
    return [];
  }

  const query = {
    raw: options.rawQuery,
    normalised: parsedQuery.normalised,
    tokens,
    rawTokens: parsedQuery.rawTokens,
    addressNumber: parsedQuery.addressNumber?.token ?? null,
    range: parsedQuery.range,
    postcode: parsedQuery.postcode,
    allowsUnverifiedPostcode:
      parsedQuery.postcode !== null && hasAddressContextForUnverifiedPostcode(tokens),
    terminalPrefixTokens: terminalPrefixTokensForQuery(tokens),
    unitValue: parsedQuery.unitValue,
    hasUnitIntent: parsedQuery.hasUnitIntent
  };
  const deduped = new Map<string, CompactShardAddress>();

  for (const record of records) {
    deduped.set(record.id, record);
  }

  const ranked = [...deduped.values()]
    .map((record) => {
      const score = scoreRecord(record, query);
      return score === null ? null : toRankedCandidate(record, score, options.datasetVersion);
    })
    .filter((candidate): candidate is RankedStreetKitCandidate => candidate !== null)
    .sort(compareRankedCandidates);

  return ranked.slice(0, options.limit);
}

export function toPublicSearchResult(candidate: RankedStreetKitCandidate): StreetKitSearchResult {
  const { record: _record, ...result } = candidate;
  return result;
}

type QueryContext = {
  raw: string;
  normalised: string;
  tokens: string[];
  rawTokens: string[];
  addressNumber: string | null;
  range: ParsedAddressRange | null;
  postcode: string | null;
  allowsUnverifiedPostcode: boolean;
  terminalPrefixTokens: Set<string>;
  unitValue: string | null;
  hasUnitIntent: boolean;
};

type RecordContext = {
  record: CompactShardAddress;
  tokens: Set<string>;
  tokenList: string[];
  label: string;
  line1: string;
  road: string;
  suburb: string;
  town: string;
  ta: string;
  postcode: string;
  number: string;
  unit: string;
  orderedTokens: string[];
  roadTokens: string[];
  suburbTokens: string[];
  townTokens: string[];
  taTokens: string[];
  roadRawTokens: string[];
  suburbRawTokens: string[];
  townRawTokens: string[];
};

type TokenMatch = {
  token: string;
  kind: "exact" | "prefix" | "partial";
};

function scoreRecord(record: CompactShardAddress, query: QueryContext): number | null {
  const context = recordContext(record);
  const matches: TokenMatch[] = [];

  for (const token of query.tokens) {
    const match = matchQueryToken(token, context, query);

    if (match === null) {
      return null;
    }

    matches.push(match);
  }

  let score = Math.round((record.rank.base ?? 0) / 2);

  score += fullTextBonus(context, query);
  score += fieldTokenBonus(context, query);
  score += addressNumberBonus(context, query.addressNumber);
  score += rangeBonus(context, query.range);
  score += unitBonus(context, query);
  score += postcodeBonus(context, query.postcode);
  score += orderedTokenBonus(query.tokens, context.orderedTokens);
  score += rawSpellingBonus(context, query.rawTokens);
  score += cleanLabelBonus(context);
  score += genericMatchBonus(matches);

  return score;
}

function toRankedCandidate(
  record: CompactShardAddress,
  score: number,
  datasetVersion: string
): RankedStreetKitCandidate {
  return {
    streetkit_id: record.id,
    label: record.label,
    line1: record.primaryText,
    ...(record.secondaryText === null ? {} : { line2: record.secondaryText }),
    ...(record.components.city === null ? {} : { town_city: record.components.city }),
    ...(record.components.territorialAuthority === null
      ? {}
      : { territorial_authority: record.components.territorialAuthority }),
    ...(record.components.postcode === null ? {} : { postcode: record.components.postcode }),
    dataset_version: datasetVersion,
    score,
    record
  };
}

function fullTextBonus(context: RecordContext, query: QueryContext): number {
  let score = 0;

  if (context.label === query.normalised) {
    score += 80;
  } else if (context.label.startsWith(query.normalised)) {
    score += 44;
  }

  if (context.line1 === query.normalised) {
    score += 58;
  } else if (context.line1.startsWith(query.normalised)) {
    score += 36;
  }

  if (context.road === query.normalised) {
    score += 48;
  } else if (context.road.startsWith(query.normalised)) {
    score += 28;
  }

  if (context.suburb === query.normalised) {
    score += 22;
  } else if (context.suburb.startsWith(query.normalised)) {
    score += 12;
  }

  if (context.town === query.normalised) {
    score += 18;
  } else if (context.town.startsWith(query.normalised)) {
    score += 10;
  }

  return score;
}

function fieldTokenBonus(context: RecordContext, query: QueryContext): number {
  let score = 0;

  for (const token of query.tokens) {
    if (shouldSkipFieldTokenBonus(token, query)) {
      continue;
    }

    score += tokenFieldScore(token, context.roadTokens, 30, 18);
    score += tokenFieldScore(token, context.suburbTokens, 13, 8);
    score += tokenFieldScore(token, context.townTokens, 11, 7);
    score += tokenFieldScore(token, context.taTokens, 3, 1);
  }

  return score;
}

function tokenFieldScore(
  token: string,
  fieldTokens: string[],
  exactScore: number,
  prefixScore: number
): number {
  if (fieldTokens.includes(token)) {
    return exactScore;
  }

  return token.length >= 3 && fieldTokens.some((fieldToken) => fieldToken.startsWith(token))
    ? prefixScore
    : 0;
}

function addressNumberBonus(context: RecordContext, addressNumber: string | null): number {
  if (addressNumber === null) {
    return 0;
  }

  if (context.number === addressNumber) {
    return 64;
  }

  if (context.tokens.has(addressNumber)) {
    return 38;
  }

  if (addressNumber.length >= 2 && context.number.startsWith(addressNumber)) {
    return 26;
  }

  return -36;
}

function rangeBonus(context: RecordContext, range: ParsedAddressRange | null): number {
  if (range === null) {
    return 0;
  }

  const hasLow = context.tokens.has(range.low.token);
  const hasHigh = context.tokens.has(range.high.token);

  if (hasLow && hasHigh) {
    return 72;
  }

  if (hasLow || hasHigh) {
    return 18;
  }

  return -30;
}

function unitBonus(context: RecordContext, query: QueryContext): number {
  if (query.hasUnitIntent) {
    if (query.unitValue !== null && context.unit.length > 0) {
      return context.unit === query.unitValue ? 68 : -38;
    }

    return context.unit.length === 0 ? -42 : 12;
  }

  return context.unit.length === 0 ? 14 : -10;
}

function postcodeBonus(context: RecordContext, postcode: string | null): number {
  if (postcode === null) {
    return 0;
  }

  return context.postcode === postcode ? 44 : -28;
}

function cleanLabelBonus(context: RecordContext): number {
  let score = Math.max(0, 14 - Math.floor(context.record.label.length / 8));

  if (context.unit.length === 0) {
    score += 5;
  }

  if (context.number.includes("-")) {
    score -= 10;
  }

  if (context.record.label.includes(",,")) {
    score -= 6;
  }

  return score;
}

function genericMatchBonus(matches: TokenMatch[]): number {
  return matches.reduce((score, match) => {
    if (match.kind === "exact") {
      return score + 6;
    }

    if (match.kind === "prefix") {
      return score + 2;
    }

    return score - 16;
  }, 0);
}

function orderedTokenBonus(tokens: string[], orderedTokens: string[]): number {
  let cursor = 0;
  let matched = 0;

  for (const token of tokens) {
    const index = orderedTokens.findIndex(
      (recordToken, candidateIndex) =>
        candidateIndex >= cursor && tokenMatchesOrderedToken(token, recordToken)
    );

    if (index === -1) {
      continue;
    }

    matched += 1;
    cursor = index + 1;
  }

  return matched === tokens.length ? 22 : matched * 3;
}

function rawSpellingBonus(context: RecordContext, rawTokens: string[]): number {
  let score = 0;

  for (const token of rawTokens) {
    if (token.length < 2 || isAddressNumberToken(token) || isPostcodeToken(token)) {
      continue;
    }

    if (context.roadRawTokens.includes(token)) {
      score += 8;
    }

    if (context.suburbRawTokens.includes(token)) {
      score += 5;
    }

    if (context.townRawTokens.includes(token)) {
      score += 3;
    }
  }

  return score;
}

function matchQueryToken(
  token: string,
  context: RecordContext,
  query: QueryContext
): TokenMatch | null {
  if (isPostcodeToken(token)) {
    if (context.tokens.has(token)) {
      return { token, kind: "exact" };
    }

    if (context.postcode.length === 0 && query.allowsUnverifiedPostcode) {
      return { token, kind: "partial" };
    }

    return null;
  }

  if (isAddressNumberToken(token)) {
    if (context.tokens.has(token)) {
      return { token, kind: "exact" };
    }

    if (token.length >= 2 && context.number.startsWith(token)) {
      return { token, kind: "prefix" };
    }

    return null;
  }

  if (context.tokens.has(token)) {
    return { token, kind: "exact" };
  }

  if (isSaintPlaceAbbreviationMatch(token, context, query)) {
    return { token, kind: "exact" };
  }

  if (
    (token.length >= 3 || query.terminalPrefixTokens.has(token)) &&
    context.tokenList.some((recordToken) => recordToken.startsWith(token))
  ) {
    return { token, kind: "prefix" };
  }

  if (token.length >= 4 && context.orderedTokens.join(" ").includes(token)) {
    return { token, kind: "partial" };
  }

  return null;
}

function isSaintPlaceAbbreviationMatch(
  token: string,
  context: RecordContext,
  query: QueryContext
): boolean {
  if (token !== "street") {
    return false;
  }

  const placeFollowers = saintAbbreviationFollowers(query.rawTokens);

  if (placeFollowers.length === 0) {
    return false;
  }

  return placeFollowers.some(
    (follower) =>
      startsWithSaintPlace(context.suburbTokens, follower) ||
      startsWithSaintPlace(context.townTokens, follower) ||
      startsWithSaintPlace(context.taTokens, follower)
  );
}

function saintAbbreviationFollowers(rawTokens: string[]): string[] {
  const followers: string[] = [];

  for (let index = 0; index < rawTokens.length - 1; index += 1) {
    const token = rawTokens[index];
    const next = rawTokens[index + 1];

    if (token === "st" && next !== undefined && /^[a-z]{2,}$/u.test(next)) {
      followers.push(next);
    }
  }

  return followers;
}

function startsWithSaintPlace(tokens: string[], follower: string): boolean {
  return tokens[0] === "saint" && tokens[1] === follower;
}

function tokenMatchesOrderedToken(token: string, recordToken: string): boolean {
  return token === recordToken || (token.length >= 3 && recordToken.startsWith(token));
}

function terminalPrefixTokensForQuery(tokens: string[]): Set<string> {
  const token = tokens.at(-1);

  if (
    token === undefined ||
    token.length > 2 ||
    !/^[a-z]+$/u.test(token) ||
    isUnitMarker(token)
  ) {
    return new Set();
  }

  return new Set([token]);
}

function hasAddressContextForUnverifiedPostcode(tokens: string[]): boolean {
  const nonPostcodeTokens = tokens.filter((token) => !isPostcodeToken(token));
  return (
    nonPostcodeTokens.some(isAddressNumberToken) &&
    nonPostcodeTokens.some((token) => !isAddressNumberToken(token) && !isUnitMarker(token))
  );
}

function recordContext(record: CompactShardAddress): RecordContext {
  const road = record.components.street ?? "";
  const suburb = record.components.suburb ?? "";
  const town = record.components.city ?? "";
  const ta = record.components.territorialAuthority ?? "";
  const postcodeValue = record.components.postcode ?? "";
  const numberValue = [record.components.number, record.components.numberSuffix]
    .filter(hasText)
    .join("");
  const unitValue = record.components.unit ?? "";
  const roadTokens = tokeniseAddressQuery(road);
  const suburbTokens = tokeniseAddressQuery(suburb);
  const townTokens = tokeniseAddressQuery(town);
  const taTokens = tokeniseAddressQuery(ta);
  const postcode = normaliseAddressQuery(postcodeValue);
  const number = normaliseAddressQuery(numberValue);
  const unit = normaliseAddressQuery(unitValue);
  const orderedTokens = tokeniseAddressQuery(
    [
      record.primaryText,
      record.secondaryText,
      record.components.suburb,
      record.components.city,
      record.components.territorialAuthority,
      record.components.postcode
    ]
      .filter(hasText)
      .join(" ")
  );
  const tokens = normalisedRecordTokens(record);

  for (const value of [
    record.label,
    record.primaryText,
    record.secondaryText,
    numberValue,
    record.components.numberHigh,
    unitValue,
    road,
    suburb,
    town,
    ta,
    postcodeValue
  ]) {
    for (const token of tokeniseAddressQuery(value ?? "")) {
      tokens.add(token);
    }
  }

  return {
    record,
    tokens,
    tokenList: [...tokens],
    label: normaliseAddressQuery(record.label),
    line1: normaliseAddressQuery(record.primaryText),
    road: normaliseAddressQuery(road),
    suburb: normaliseAddressQuery(suburb),
    town: normaliseAddressQuery(town),
    ta: normaliseAddressQuery(ta),
    postcode,
    number,
    unit,
    orderedTokens,
    roadTokens,
    suburbTokens,
    townTokens,
    taTokens,
    roadRawTokens: tokeniseRawText(road),
    suburbRawTokens: tokeniseRawText(suburb),
    townRawTokens: tokeniseRawText(town)
  };
}

function shouldSkipFieldTokenBonus(token: string, query: QueryContext): boolean {
  return (
    token === query.addressNumber ||
    token === query.postcode ||
    isUnitMarker(token) ||
    isAddressNumberToken(token) ||
    isPostcodeToken(token)
  );
}

function normalisedRecordTokens(record: CompactShardAddress): Set<string> {
  const tokens = new Set<string>();

  for (const token of [
    ...(record.search.tokens ?? []),
    ...(record.search.aliases ?? []),
    ...(record.search.numberTokens ?? []),
    ...(record.search.unitTokens ?? []),
    ...(record.search.rangeTokens ?? [])
  ]) {
    for (const normalisedToken of tokeniseAddressQuery(token)) {
      tokens.add(normalisedToken);
    }
  }

  return tokens;
}

function tokeniseRawText(value: string): string[] {
  return parseAddressQuery(value).rawTokens;
}

function compareRankedCandidates(
  left: RankedStreetKitCandidate,
  right: RankedStreetKitCandidate
): number {
  const scoreOrder = right.score - left.score;

  if (scoreOrder !== 0) {
    return scoreOrder;
  }

  const labelOrder = compareStableText(left.label, right.label);

  if (labelOrder !== 0) {
    return labelOrder;
  }

  return compareStableText(left.streetkit_id, right.streetkit_id);
}

function compareStableText(left: string, right: string): number {
  if (left < right) {
    return -1;
  }

  if (left > right) {
    return 1;
  }

  return 0;
}

function hasText(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
